import { z } from 'zod'

/**
 * SPEC.md §4.4 — the role and permission matrix.
 *
 * A role is per server, held in `server_members.role`. `can()` is the single
 * authority used by the API `requireRole` preHandler and by the panel to hide
 * controls, so the two can never disagree.
 */

export const ROLES = ['owner', 'admin', 'moderator', 'viewer'] as const
export const RoleSchema = z.enum(ROLES)
export type Role = (typeof ROLES)[number]

export const MEMBER_STATUSES = ['active', 'invited'] as const
export const MemberStatusSchema = z.enum(MEMBER_STATUSES)
export type MemberStatus = (typeof MEMBER_STATUSES)[number]

export const PERMISSIONS = {
  'server.read': ['owner', 'admin', 'moderator', 'viewer'],
  'detections.read': ['owner', 'admin', 'moderator', 'viewer'],
  'detections.review': ['owner', 'admin', 'moderator'],
  'players.read': ['owner', 'admin', 'moderator', 'viewer'],
  'players.notes': ['owner', 'admin', 'moderator'],
  'bans.read': ['owner', 'admin', 'moderator', 'viewer'],
  'bans.write': ['owner', 'admin', 'moderator'],
  'bans.import': ['owner', 'admin'],
  'bans.propose_global': ['owner'],
  'screenshots.read': ['owner', 'admin', 'moderator', 'viewer'],
  'screenshots.request': ['owner', 'admin', 'moderator'],
  'commands.kick': ['owner', 'admin', 'moderator'],
  'commands.message': ['owner', 'admin', 'moderator'],
  'commands.reload': ['owner', 'admin'],
  'whitelist.write': ['owner', 'admin'],
  'config.read': ['owner', 'admin', 'moderator', 'viewer'],
  'config.write': ['owner', 'admin'],
  'discord.manage': ['owner', 'admin'],
  'members.manage': ['owner', 'admin'],
  'audit.read': ['owner', 'admin'],
  'license.manage': ['owner'],
  'server.write': ['owner', 'admin'],
  'server.delete': ['owner'],
  'globalbans.manage': ['owner'],
} as const satisfies Record<string, readonly Role[]>

export type Permission = keyof typeof PERMISSIONS

export const PERMISSION_KEYS = Object.keys(PERMISSIONS) as [Permission, ...Permission[]]
export const PermissionSchema = z.enum(PERMISSION_KEYS)

export function can(role: Role, permission: Permission): boolean {
  return (PERMISSIONS[permission] as readonly Role[]).includes(role)
}

/** Every permission a role holds — used to ship one compact object to the panel. */
export function permissionsFor(role: Role): Permission[] {
  return PERMISSION_KEYS.filter((p) => can(role, p))
}

/** Ranking used for "can this member manage that member" checks. Higher outranks lower. */
export const ROLE_RANK: Record<Role, number> = { owner: 3, admin: 2, moderator: 1, viewer: 0 }

export function outranks(actor: Role, target: Role): boolean {
  return ROLE_RANK[actor] > ROLE_RANK[target]
}

/**
 * API key scopes (SPEC.md §3.2 `api_keys.scopes`). A key can never exceed the
 * permissions of the user who owns it; scopes only narrow them further.
 */
export const API_KEY_SCOPES = [
  'servers:read',
  'detections:read',
  'players:read',
  'bans:read',
  'bans:write',
  'commands:write',
  'config:read',
] as const
export const ApiKeyScopeSchema = z.enum(API_KEY_SCOPES)
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number]

/** The permission an API key scope maps onto, so one check covers both auth paths. */
export const SCOPE_PERMISSION: Record<ApiKeyScope, Permission> = {
  'servers:read': 'server.read',
  'detections:read': 'detections.read',
  'players:read': 'players.read',
  'bans:read': 'bans.read',
  'bans:write': 'bans.write',
  'commands:write': 'commands.kick',
  'config:read': 'config.read',
}
